import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get } from 'lodash-es';
import Col from 'reactstrap/lib/Col';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';

/* MODELS */
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { buildReviewPath, EditorialReviewEntities, EditorialReviewModel } from 'client/data/models/editorial-review';
import { VisitorEntities, VisitorModel } from 'client/data/models/visitor';
import { FeatureSpecsPaths, VehicleEntities, VehicleModel } from 'client/data/models/vehicle-v2';

/* UTILS */
import { cloneParamsWithPubState } from 'site-modules/shared/utils/core-page/page-helpers';
import { CorePageParams, getParamsFromVehicle } from 'site-modules/shared/utils/core-page/params';
import { buildDisplayVehicle } from 'site-modules/shared/utils/core-page/display-name-builders';
import { getPublicationStateForMakeModel } from 'site-modules/shared/utils/publication-states';
import { buildCoreBreadcrumbs } from 'site-modules/shared/utils/core-breadcrumbs-builder';
import { isUnknownZip } from 'site-modules/shared/utils/location';
import { getEditorialIcon } from 'site-modules/shared/utils/core-page/rating-and-ranking';
import { getOverallRating } from 'site-modules/shared/utils/editorial-helper';

/* ADS */
import { NativeAd } from 'site-modules/shared/components/ad-unit/native-ad';
import { MrectNative } from 'site-modules/shared/components/native-ad/mrect/mrect-native';
import { BuildPriceAd } from 'site-modules/shared/components/native-ad/buildprice-ad/buildprice-ad';

/* TRACKING */
import { TrackingMap } from 'client/tracking/maps/core-page';
import { TrackingMap as TrackingVDPMap } from 'client/tracking/maps/inventory/vdp/index';
import { TrackingMap as TrackingSRPMap } from 'client/tracking/maps/inventory/srp/index';
import { TrackingMap as TrackingSaveModelMap } from 'client/tracking/maps/insider/save-model/index';
import { EditorialMap } from 'client/tracking/maps/editorial';
import { TrackingHandler } from 'client/tracking/handler';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';

/* UTIL COMPONENTS */
import { HashAnchor } from 'site-modules/shared/components/hash-anchor/hash-anchor';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';

/* SHARED COMPONENTS */
import { Breadcrumbs } from 'site-modules/shared/components/breadcrumbs/breadcrumbs';
import { VideoVehicleModule } from 'site-modules/shared/components/video-module/video-module';
import { ConsumerReviewsModular } from 'site-modules/shared/components/consumer-reviews-modular/consumer-reviews-modular';
import { SponsoredCars } from 'site-modules/shared/components/core-page/sponsored-cars/sponsored-cars';
import { BackToTop } from 'site-modules/shared/components/back-to-top/back-to-top';
import { PageLinksWidget } from 'site-modules/shared/components/page-links-widget/page-links-widget';
import { MakeColumnNewsWidget } from 'site-modules/shared/components/column-news-widget/column-news-widget';

/* COMPONENTS */
import { Safety } from 'site-modules/core-page/components/safety/safety';
import { EditorialReview } from 'site-modules/core-page/components/editorial-review/editorial-review';
import { ScorecardEditorialReview } from 'site-modules/core-page/components/scorecard-editorial-review/scorecard-editorial-review';
import { UsedCoreIntro } from 'site-modules/core-page/components/used-core-intro/used-core-intro';
import { RangeAndCost } from 'site-modules/core-page/components/range-and-cost/range-and-cost';
import { FeaturesHighlights } from 'site-modules/core-page/components/features-highlights/features-highlights';
import { EvInsights } from 'site-modules/core-page/components/ev-insights/ev-insights';
import { RatingScorecardEmbed } from 'site-modules/core-page/components/scorecard-editorial-review/rating-scorecard/rating-scorecard-embed';
import { VideoReviews } from 'site-modules/core-page/components/video-reviews/video-reviews';
import { Reliability } from 'site-modules/core-page/components/reliability/reliability';
import { OtherYears, OtherYearsGraphql } from 'site-modules/core-page/components/other-years/other-years';
import { OtherSubmodels } from 'site-modules/core-page/components/other-submodels/other-submodels';
import { CoreSrp } from 'site-modules/core-page/components/core-srp/core-srp';
import { UsedCoreSubnav } from 'site-modules/core-page/components/used-core-subnav/used-core-subnav';
import { PhotoGallery } from 'site-modules/core-page/components/photo-gallery/photo-gallery';

/* CONSTANTS */
import { OLD_REVIEW_FORMAT_LAST_YEAR } from 'site-modules/shared/constants/editorial-review';
import { CORE_HEADER_SRC_INLINE } from 'site-modules/core-page/constants/core-page';
import { MRECT_WIRED_CUSTOM_SIZE } from 'site-modules/shared/components/ad-unit/context/sizes';
import { MENU_HASH } from 'site-modules/shared/constants/core-page/menu-hash';

/* CSS */
import './used-core.scss';

/* LAZY IMPORTS */
import lazyImports from './lazy-imports';

const { AlsoViewedVehiclesCrossShop } = lazyImports;

const HEADER_HEIGHT = { true: '550px', false: '535px' };

function UsedCoreUI({
  vehicle = null,
  editorialReview = null,
  visitorLocation = {},
  params: propsParams,
  styleAttributes = null,
  isMobile = false,
  isCore7646Enabled = false,
}) {
  const isTracked = useRef({ editorialRating: false });
  const { isNoSubmodelPage, year: urlYear } = propsParams;

  useEffect(() => {
    TrackingHandler.useMap({
      ...TrackingMap,
      ...TrackingVDPMap,
      ...TrackingSRPMap,
      ...TrackingSaveModelMap,
      ...EditorialMap,
    });
  }, []);

  const hasEditorialReview = !!editorialReview;
  const overallRating = getOverallRating(editorialReview);
  // content tracking
  useEffect(() => {
    // editorial rating
    if (!isTracked.current.editorialRating && hasEditorialReview) {
      isTracked.current.editorialRating = true;

      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
          action_name: TrackingConstant.ACTION_SHOW_CONTENT,
          synpartner: TrackingConstant.SYNPARTNER,
          action_cause: TrackingConstant.PAGE_LOAD_CAUSE,
          page_name: 'used_model_core',
          page_category: 'used_model_core',
          subaction_name: TrackingConstant.RATING_CATEGORY,
          value: overallRating ? getEditorialIcon(overallRating).text : 'no rating',
        },
      });
    }
  }, [hasEditorialReview, overallRating]);

  if (!(vehicle && vehicle.make && vehicle.model && vehicle.submodels && vehicle.year && vehicle.pubStates)) {
    return (
      <main id="main-content" tabIndex={-1} className="core-page loading">
        <Container>Loading&hellip;</Container>
      </main>
    );
  }

  const params = cloneParamsWithPubState({
    params: {
      ...propsParams,
      ...(isCore7646Enabled
        ? {
            isElectric: !!vehicle?.styleAttributes?.isElectric,
            isPluginHybrid: !!vehicle?.styleAttributes?.isPluginHybrid,
            isTruck: !!vehicle?.styleAttributes?.isTruck,
          }
        : {
            isElectric: !!styleAttributes?.electric,
            isPluginHybrid: !!styleAttributes?.pluginElectric,
            isTruck: !!styleAttributes?.truck,
          }),
    },
    year: vehicle.year,
    isUsed: true,
  });

  const {
    make: { name: makeName, slug: makeSlug },
    model: { name: modelName, slug: modelSlug, modelLinkCode },
    year,
    submodels: { slug: submodelSlug },
    modelYearId,
  } = vehicle;

  const displayVehicle = buildDisplayVehicle({
    makeModelSubmodelYear: vehicle,
    params,
    disableTrademarkCharacter: true,
  });
  const isInternational = isUnknownZip(visitorLocation);
  const OtherYearsComponent = isCore7646Enabled ? OtherYearsGraphql : OtherYears;

  const pubStateLowercase = getPublicationStateForMakeModel(vehicle.pubStates);
  const noEditorialReview = !(get(editorialReview, 'reviewLink') || get(editorialReview, 'hasRelatedArticles'));
  const hasScorecardReview = !!getOverallRating(editorialReview);

  const coreSrpModule = (
    <RatingScorecardEmbed childrenClassName="mb-2" isEmbed={hasScorecardReview}>
      <ScrollElement tag={Container} id={MENU_HASH.FOR_SALE} className="px-0">
        <CoreSrp vehicleParams={{ makeName, modelName, makeSlug, modelSlug, year }} isMobile={isMobile} showUsurpAd />
      </ScrollElement>
    </RatingScorecardEmbed>
  );

  const buildPriceAdModule = (
    <BuildPriceAd mobile={isMobile} wrapperClass="d-flex justify-content-center mb-2" includeMrectAdCall />
  );

  const shortVideosModule = (
    <VideoReviews
      className="mt-2 mb-2"
      params={params}
      vehicleName={`${year} ${makeName} ${modelName}`}
      creativeId="video-carousel"
    />
  );

  const newsModule = (
    <MakeColumnNewsWidget
      makeSlug={makeSlug}
      makeName={makeName}
      modelName={modelName}
      className="my-1_5"
      creativeId="news_module"
    />
  );

  return (
    <main id="main-content" tabIndex={-1} className="core-page pos-r pt-0_75 pt-md-1_5">
      <img
        src={CORE_HEADER_SRC_INLINE[isMobile]}
        alt=""
        fetchPriority="high"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          maxHeight: HEADER_HEIGHT[isMobile],
        }}
      />
      <UsedCoreIntro
        params={{
          ...params,
          noEditorialReview,
        }}
        vehicle={vehicle}
        editorialReview={editorialReview}
        isInternational={isInternational}
        isMobile={isMobile}
        isCore7646Enabled={isCore7646Enabled}
      />
      <Container className="mb-3">
        <Row>
          <Col xs={12} lg={8}>
            <div className="intro-other-years-pills">
              <OtherYearsComponent
                renderEmptyHidden
                vehicle={vehicle}
                isMobile={isMobile}
                heading={`${makeName} ${modelName} model years`}
              />
              <OtherSubmodels
                className="mt-1_5"
                params={params}
                heading={`${makeName} ${modelName} types`}
                currentSubmodelId={`${year}|${submodelSlug}`}
              />
            </div>
          </Col>
        </Row>
        <UsedCoreSubnav className="mt-3" params={params} vehicle={vehicle} isMobile={isMobile} />
      </Container>
      <ScrollElement tag={Container} id={MENU_HASH.REVIEWS}>
        <HashAnchor className="core-page-anchor" name="review" />
        {editorialReview &&
          (propsParams.year > OLD_REVIEW_FORMAT_LAST_YEAR ? (
            <ScorecardEditorialReview
              vehicle={vehicle}
              params={params}
              hasRouteYearParam={!!urlYear}
              isUsed
              showHighlights
              hasTrimsLink
              embeddedModules={{ coreSrpModule, buildPriceAdModule, shortVideosModule, newsModule }}
            />
          ) : (
            <EditorialReview
              vehicle={vehicle}
              params={params}
              hasRouteYearParam={!!urlYear}
              isUsed
              showHighlights
              showBodyScorecard
              expandReview
              isReordered
              embeddedModules={{ coreSrpModule, buildPriceAdModule }}
            />
          ))}
        <Row className="mx-0">
          <Col xs={12} lg={8}>
            {params.isElectric ? (
              <EvInsights vehicle={vehicle} isMobile={isMobile} isUsed />
            ) : (
              <RangeAndCost params={params} vehicle={vehicle} className="mb-1_5" isMobile={isMobile} />
            )}
          </Col>
        </Row>
        {isMobile && (
          <NativeAd
            wrapperClass="mrect mx-auto mb-3_5"
            adNameWired="mrect"
            adNameMobile="mmrect"
            nativeStyle="mediumrectangle"
            showLabel
            refreshable={false}
          />
        )}
        <Row>
          <Col xs={12} lg={8}>
            {!isMobile && (
              <NativeAd
                wrapperClass="mrect mx-auto mb-3_5"
                adNameWired="mrect"
                adNameMobile="mmrect"
                nativeStyle="mediumrectangle"
                showLabel
                refreshable={false}
              />
            )}
            <Reliability
              className="mb-3_5"
              vehicleParams={{ makeSlug, makeName, modelSlug, modelName, submodelSlug, year, modelYearId }}
              isMobile={isMobile}
              isUsed
            />
            {isMobile && <MrectNative wrapperClass="mb-2" position={'2'} refreshable={false} />}
          </Col>
        </Row>
        <Row className="mb-2">
          <HashAnchor className="core-page-anchor" name="consumer-reviews" />
          <Col xs={12} lg={8} className="d-flex flex-column justify-content-between">
            <ScrollElement id={MENU_HASH.CONSUMER_REVIEWS}>
              <ConsumerReviewsModular
                vehicle={vehicle}
                isMobile={isMobile}
                creativeId="consumer-reviews"
                withReviewsSummary
              />
            </ScrollElement>
          </Col>
          <Col xs={12} lg={4}>
            {!isMobile && (
              <MrectNative position={'2'} refreshable={false} addCustomSizesWired={MRECT_WIRED_CUSTOM_SIZE} />
            )}
          </Col>
        </Row>
        <ScrollElement id={MENU_HASH.VIDEOS}>
          <VideoVehicleModule
            isMobile={isMobile}
            vehicle={vehicle}
            videoLargeColumnSize={8}
            playlistLargeColumnSize={4}
            pageName={`${pubStateLowercase}_model_core`}
            isModularDesign
          />
        </ScrollElement>
      </ScrollElement>
      <ScrollElement id={MENU_HASH.FEATURES}>
        <Container>
          <Row>
            <Col xs={12} lg={8}>
              <FeaturesHighlights params={params} vehicle={vehicle} isMobile={isMobile} />
            </Col>
          </Row>
          <hr className="mt-1_5 mb-2" />
          <Row>
            <Col xs={12} lg={8}>
              <Safety modelName={modelName} editorialReview={editorialReview} params={params} />
              {isMobile && (
                <section className="mb-3_5" data-tracking-parent="interior-photos">
                  <h2 className="heading-3 mb-1">Interior Photos</h2>
                  <PhotoGallery
                    params={params}
                    vehicle={vehicle}
                    editorialReview={editorialReview}
                    hideHeroPhoto={isMobile}
                    isMobile={isMobile}
                  />
                </section>
              )}
              <AlsoViewedVehiclesCrossShop modelLinkCode={modelLinkCode} lazyLoadImages={isNoSubmodelPage} isUsed />
            </Col>
          </Row>
        </Container>
      </ScrollElement>
      <Container>
        <Experiment name="ads-12460_dfp" showDefault>
          <Recipe name="ctrl" isDefault>
            <SponsoredCars />
          </Recipe>
          <Recipe name="chal">
            <SponsoredCars showStackedAds inlineStyle={isMobile ? null : { maxWidth: '744px' }} />
          </Recipe>
        </Experiment>
        <Breadcrumbs
          className="ps-0 mb-2"
          data={buildCoreBreadcrumbs({
            makeModelSubmodelYear: vehicle,
            params: propsParams,
            isMainCorePage: true,
            includeLastLink: true,
          })}
        />
      </Container>

      <PageLinksWidget divideSections={false} title={`Related ${displayVehicle} info`} className="container mb-3" />
      <BackToTop />
      <Experiment name="core-7591-used" showDefault>
        <Recipe name="ctrl" isDefault />
        <Recipe name="chal" />
      </Experiment>
    </main>
  );
}

UsedCoreUI.propTypes = {
  params: CorePageParams.isRequired,
  vehicle: VehicleEntities.MakeModelSubmodelYear,
  editorialReview: EditorialReviewEntities.EditorialReview,
  visitorLocation: VisitorEntities.Location,
  styleAttributes: PropTypes.objectOf(PropTypes.bool),
  isMobile: PropTypes.bool,
  vinsData: PropTypes.shape({
    totalNumber: PropTypes.number,
  }),
  isCore7646Enabled: PropTypes.bool,
};

export const mapStateToProps = state => ({
  isMobile: state.mobile,
  isCore7646Enabled: !!state.featureFlags?.['core-7646-used-graphql-vehicle'],
});

/*
 * Please keep these binds to a minimum and only to page-level binds.
 * Component-level binds (binds needed by components) should be done
 * within the components themselves. This also ensures components are
 * completely standalone and reusable.
 */
export const UsedCore = connect(mapStateToProps)(
  connectToModel(UsedCoreUI, {
    visitorLocation: bindToPath('location', VisitorModel),
    editorialReview: bindToPath(
      ({ vehicle }) => vehicle && buildReviewPath(getParamsFromVehicle(vehicle)),
      EditorialReviewModel
    ),
    styleAttributes: bindToPath(
      ({ vehicle, isCore7646Enabled }) =>
        !isCore7646Enabled && vehicle && FeatureSpecsPaths.buildPartialFeaturesPath(getParamsFromVehicle(vehicle)),
      VehicleModel,
      styles => get(styles, '[0].styleAttributes')
    ),
  })
);

UsedCore.propTypes = {
  params: CorePageParams.isRequired,
  vehicle: VehicleEntities.MakeModelSubmodelYear,
};
